import { EventEmitter, NgZone } from '@angular/core';
import { TrackType } from 'src/app/common/interfaces/rtc-interface';
import { modifiedSetInterval } from 'src/app/utilities/ZoneUtils';
import { FullStory } from '@fullstory/browser';

interface AudioRender {
  polling?: NodeJS.Timer;
  audioContext?: AudioContext;
}

export interface CurrentlyDetectingAudio {
  trackType: TrackType;
  isCurrentlyDetected: boolean;
  meanAmplitude?: number | string;
  medianAmplitude?: number | string;
  frequencyData?: Array<Array<number>>;
  sampleRate?: number;
}

// Checks the volume of a MediaStreamTrack and if it's above
// a threshold then it emits an event using an EventEmitter
export class VolumeDetector extends EventEmitter<CurrentlyDetectingAudio> {
  private audioRender?: AudioRender;
  private lastEmittedCurrentlyDetectingAudio?: Partial<CurrentlyDetectingAudio>;
  private _audioTrack?: MediaStreamTrack;
  private _calculateSpeechStats = false;
  private _amplitudeDuringSpeech: number[] = [];
  private _lastFrequencyCaptureTime?: Date;
  private _frequencyData: Array<Array<number>> = [];
  constructor(
    private readonly threshold: number,
    private ngZone: NgZone,
    enableSpeechStats?: boolean,
  ) {
    super();
    if (enableSpeechStats !== undefined) {
      this.calculateSpeechStats = enableSpeechStats;
    }
  }

  public set calculateSpeechStats(value: boolean) {
    this._calculateSpeechStats = value;
  }

  public startAudioRender(audioTrack: MediaStreamTrack, trackType: TrackType): void {
    // Added to avoid any weirdness from having two volume detectors on the same track
    this._audioTrack = audioTrack.clone();
    const context = window.AudioContext || (window as any).webkitAudioContext;

    if (!context) {
      return;
    }

    if (this.audioRender) {
      this.stopAudioRenderer(trackType);
    }

    const audioContext = new context();
    const audioAnalyzer = audioContext.createAnalyser();
    audioAnalyzer.smoothingTimeConstant = 0.3;
    audioAnalyzer.fftSize = 32;
    // Added to avoid having a disabled audio track while mic is muted as LiveKit is changing this property on mute state
    // Also it won't hurt as we are cloning the track entirely once starting the audio renderer
    // So, acutal broadcasted track won't be affected
    this._audioTrack.enabled = true;
    const audioStream = new MediaStream([this._audioTrack]);
    const source = audioContext.createMediaStreamSource(audioStream);
    source.connect(audioAnalyzer);
    const frequencyData = new Uint8Array(audioAnalyzer.frequencyBinCount);
    audioAnalyzer.getByteFrequencyData(frequencyData);

    // Returns amplitude values of the audio signal
    const audioAmplitudeData = new Float32Array(audioAnalyzer.frequencyBinCount);

    this.ngZone.runOutsideAngular(() => {
      const polling = modifiedSetInterval(() => {
        audioAnalyzer.getByteFrequencyData(frequencyData);
        const volume = frequencyData.reduce((acc, curr) => acc + curr);

        const currentlyDetectingAudio: CurrentlyDetectingAudio = {
          trackType: trackType,
          isCurrentlyDetected: false,
        };

        if (volume > this.threshold) {
          currentlyDetectingAudio.isCurrentlyDetected = true;
          if (this._calculateSpeechStats) {
            audioAnalyzer.getFloatTimeDomainData(audioAmplitudeData);
            this._addAmplitudeData(audioAmplitudeData);
            if (
              !this._lastFrequencyCaptureTime ||
              new Date().getTime() - this._lastFrequencyCaptureTime.getTime() > 1000
            ) {
              const frequencyArray = Array.from(frequencyData);
              this._frequencyData.push(frequencyArray);
              this._lastFrequencyCaptureTime = new Date();
            }
          }
        } else {
          currentlyDetectingAudio.isCurrentlyDetected = false;
        }

        if (
          this.lastEmittedCurrentlyDetectingAudio?.isCurrentlyDetected !==
          currentlyDetectingAudio.isCurrentlyDetected
        ) {
          this.lastEmittedCurrentlyDetectingAudio = currentlyDetectingAudio;
          // Only emits if there is a change & run this emission inside zone
          this.ngZone.run(() => {
            // Don't emit speech stats if the user just started speaking or if its disabled
            if (!this._calculateSpeechStats || currentlyDetectingAudio.isCurrentlyDetected) {
              this.emit(currentlyDetectingAudio);
              return;
            }

            // Otherwise, calculate the mean and median amplitude and emit
            const meanAmplitude = this._getMeanAmplitude();
            const medianAmplitude = this._getMedianAmplitude();
            this._resetAmplitudeData();
            this.emit({
              ...currentlyDetectingAudio,
              meanAmplitude,
              medianAmplitude,
              frequencyData: this._frequencyData,
              sampleRate: audioContext.sampleRate,
            });
            this._resetFrequencyData();
          });
        }
      }, 200);

      this.audioRender = { audioContext, polling };
    });
  }

  public stopAudioRenderer(trackType: TrackType): void {
    if (this.audioRender?.polling) {
      clearInterval(this.audioRender.polling);
    }
    const sampleRate = this.audioRender?.audioContext?.sampleRate;
    if (this.audioRender?.audioContext) {
      this.audioRender.audioContext.close();
    }
    const currentlyDetectingAudio: CurrentlyDetectingAudio = {
      trackType: trackType,
      isCurrentlyDetected: false,
    };
    if (this.calculateSpeechStats) {
      currentlyDetectingAudio.meanAmplitude = this._getMeanAmplitude();
      currentlyDetectingAudio.medianAmplitude = this._getMedianAmplitude();
      currentlyDetectingAudio.frequencyData = this._frequencyData;
      currentlyDetectingAudio.sampleRate = sampleRate;
      this._resetAmplitudeData();
      this._resetFrequencyData();
    }
    this.emit(currentlyDetectingAudio);
    this.audioRender = undefined;
    this.lastEmittedCurrentlyDetectingAudio = undefined;
    this._audioTrack?.stop();
    this._audioTrack = undefined;
  }

  private _addAmplitudeData(amplitudeData: Float32Array): void {
    if (amplitudeData.length === 0) {
      FullStory('log', {
        level: 'debug',
        msg: '[amp] amplitude_data_empty',
      });
      return;
    }
    const rmsAmplitude = Math.sqrt(
      amplitudeData.reduce((acc, curr) => acc + curr * curr, 0) / amplitudeData.length,
    );
    this._amplitudeDuringSpeech.push(rmsAmplitude);
  }

  private _getMeanAmplitude(): number | string {
    if (this._amplitudeDuringSpeech.length === 0) {
      FullStory('log', {
        level: 'debug',
        msg: '[amp] no amplitude data',
      });
      return 'N/A';
    }
    return (
      this._amplitudeDuringSpeech.reduce((acc, curr) => acc + curr, 0) /
      this._amplitudeDuringSpeech.length
    );
  }

  private _getMedianAmplitude(): number | string {
    if (this._amplitudeDuringSpeech.length === 0) {
      return 'N/A';
    }
    this._amplitudeDuringSpeech.sort((a, b) => a - b);
    const mid = Math.floor(this._amplitudeDuringSpeech.length / 2);
    return this._amplitudeDuringSpeech.length % 2 !== 0
      ? this._amplitudeDuringSpeech[mid]
      : (this._amplitudeDuringSpeech[mid - 1] + this._amplitudeDuringSpeech[mid]) / 2;
  }

  private _resetAmplitudeData(): void {
    FullStory('log', {
      level: 'debug',
      msg: '[amp] reset_amplitude_data',
    });
    this._amplitudeDuringSpeech = [];
  }

  private _resetFrequencyData(): void {
    FullStory('log', {
      level: 'debug',
      msg: '[amp] reset_frequency_data',
    });
    this._frequencyData = [];
    this._lastFrequencyCaptureTime = undefined;
  }
}
